/**
 * Feb 2026 — Glossary cache hook.
 *
 * Singleton in-memory cache for /api/glossary. Every <GlossaryHighlight>
 * mount, every drawer open, the /glossary page itself — they all share
 * one fetch per browser tab lifetime.
 *
 * The lookup map is built once and indexed by lowercased term/alias so
 * the per-render wrapping is O(text-token-count), not O(terms × tokens).
 *
 * Jun 2026 · BUGFIX — a transient fetch error (401 race right after
 * login, 502 while the server wakes) used to permanently cache an EMPTY
 * glossary, blanking the page and every search for the whole tab
 * lifetime. Errors now never poison the cache: the in-flight marker is
 * cleared so the next call retries, and `useGlossary` quietly retries a
 * few times before surfacing a visible "try again" state.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "./apiClient";

let _cache = null;            // { categories, terms, version }
let _lookup = null;           // Map<lowercased term/alias, entry>
let _pending = null;          // in-flight promise (de-dupes parallel fetches)

const RETRY_LIMIT = 4;
const RETRY_DELAY_MS = 2500;

function _buildLookup(terms) {
  const m = new Map();
  for (const t of terms) {
    m.set(t.term.toLowerCase(), t);
    for (const a of (t.aliases || [])) m.set(a.toLowerCase(), t);
  }
  return m;
}

export function getGlossarySync() {
  return _cache;
}

export function getGlossaryLookup() {
  return _lookup;
}

export async function loadGlossary() {
  if (_cache) return _cache;
  if (!_pending) {
    _pending = api.get("/glossary")
      .then(({ data }) => {
        _cache = data;
        _lookup = _buildLookup(data.terms || []);
        return data;
      })
      .catch(() => {
        // Never cache a failure — clear the in-flight marker so the
        // next call retries. Callers get `null` and degrade gracefully
        // (GlossaryHighlight renders plain text until a retry lands).
        _pending = null;
        return null;
      });
  }
  return _pending;
}

export default function useGlossary(opts) {
  const withStatus = !!(opts && opts.withStatus);
  const [data, setData] = useState(_cache);
  const [failed, setFailed] = useState(false);
  const [kick, setKick] = useState(0);
  const tries = useRef(0);

  useEffect(() => {
    if (_cache) {
      setData(_cache);
      return;
    }
    let cancelled = false;
    let timer = null;
    const attemptLoad = () => {
      loadGlossary().then((d) => {
        if (cancelled) return;
        if (d) {
          setData(d);
          setFailed(false);
          return;
        }
        tries.current += 1;
        if (tries.current < RETRY_LIMIT) {
          timer = window.setTimeout(attemptLoad, RETRY_DELAY_MS);
        } else {
          setFailed(true);
        }
      });
    };
    attemptLoad();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [kick]);

  const retry = () => {
    tries.current = 0;
    setFailed(false);
    setKick((k) => k + 1);
  };

  return withStatus ? { data, failed, retry } : data;
}
